import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';

// Easing function for smoother start/end (Quadratic Ease-Out)
const easeOutQuad = (t: number) => t * (2 - t);

@Directive({
  selector: '[appCountUp]',
})
export class CountUpDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() targetNumber: number = 0;
  @Input() duration: number = 2000;
  @Input() decimals: number = 0;

  // Internal state is managed within the directive
  private isCounting: boolean = false;
  private observer!: IntersectionObserver;
  private isBrowser: boolean;
  private currentCount: number = 0;
  private subscription: Subscription | null = null; // Use Subscription for cleanup

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    // Set static initial value for SSG output on the server
    if (!this.isBrowser) {
      this.renderer.setProperty(this.el.nativeElement, 'innerText', this.targetNumber.toFixed(this.decimals));
    }
  }

  ngAfterViewInit() {
    if (!this.isBrowser) return;

    // --- REMOVED REDUNDANT INNERTEXT SETTING HERE ---

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isCounting) {
            this.isCounting = true;
            this.startCounting();
            // Stop observing after the animation is triggered once
            this.observer.unobserve(this.el.nativeElement);
          }
        });
      },
      { threshold: 0.8 } // Trigger when 80% of element is visible
    );

    // Set the initial state on the client side to 0, which is where the animation will start from.
    this.renderer.setProperty(this.el.nativeElement, 'innerText', this.currentCount.toFixed(this.decimals));

    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.observer?.disconnect(); // Clean up observer
    this.subscription?.unsubscribe(); // Clean up subscription
  }

  private startCounting(): void {
    // We start the count from 0 to ensure the full roll effect is visible
    const start = 0;
    const end = this.targetNumber;
    const range = end - start;
    const startTime = performance.now();
    this.currentCount = start; // Reset current count before starting

    // Update the host element's innerText on each tick
    // Using `requestAnimationFrame` based timing for smoother, browser-optimized animation steps.
    let frameId: number;

    const tick = () => {
      const elapsedTime = performance.now() - startTime;
      let progress = elapsedTime / this.duration;

      if (progress >= 1) {
        this.renderer.setProperty(this.el.nativeElement, 'innerText', end.toFixed(this.decimals));
        this.currentCount = end;
        cancelAnimationFrame(frameId);
        return;
      }

      // Apply easing to the progress
      progress = easeOutQuad(progress);

      // Calculate current value
      const currentValue = start + range * progress;

      // Direct DOM manipulation using Renderer2
      this.renderer.setProperty(this.el.nativeElement, 'innerText', currentValue.toFixed(this.decimals));

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    // --- Removed RxJS interval logic and replaced with requestAnimationFrame logic ---
    // RxJS interval(0) is not frame-rate optimized, leading to choppy updates.
  }
}
